import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Users, Trash2, Search, Edit3, ChevronUp, ChevronDown } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose
} from '../components/ui/dialog';

// shadcn/ui components
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Button } from '@/components/ui/button';

type UserRole = 'administrator' | 'coach' | 'parent';
type SortField = 'name' | 'phone' | 'email' | 'role';
type SortDirection = 'asc' | 'desc';

interface MemberRow {
  id: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  role: UserRole;
  updated_at?: string;
}

interface EditMemberModalProps {
  member: MemberRow;
  onClose: () => void;
  onMemberUpdated: (updatedMember: MemberRow) => void;
}

function EditMemberModal({ member, onClose, onMemberUpdated }: EditMemberModalProps) {
  const [firstName, setFirstName] = useState(member.first_name || '');
  const [lastName, setLastName] = useState(member.last_name || '');
  const [phoneNumber, setPhoneNumber] = useState(member.phone_number || '');
  const [role, setRole] = useState<UserRole>(member.role);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
          phone_number: phoneNumber,
          role: role
        })
        .eq('id', member.id);
      if (updateError) {
        throw updateError;
      }
      const updatedMember: MemberRow = {
        ...member,
        first_name: firstName,
        last_name: lastName,
        phone_number: phoneNumber,
        role: role
      };
      onMemberUpdated(updatedMember);
      onClose();
    } catch (err: any) {
      console.error('Error updating member:', err);
      setError(err.message || 'Failed to update member');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Member</DialogTitle>
          <DialogDescription>Edit the member details below.</DialogDescription>
        </DialogHeader>
        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-2">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <Input
              type="text"
              value={member.email || ''}
              readOnly
              className="cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <Input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <Input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <Input
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <Select value={role} onValueChange={(val: string) => setRole(val as UserRole)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="administrator">Administrator</SelectItem>
                <SelectItem value="coach">Coach</SelectItem>
                <SelectItem value="parent">Parent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="mt-6">
            <DialogClose asChild>
              <Button variant="secondary">Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ManageMembers() {
  const { user } = useAuth();

  const [members, setMembers] = useState<MemberRow[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editingMember, setEditingMember] = useState<MemberRow | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleRemoveMember = async (memberId: string) => {
    const confirmRemove = window.confirm(
      'Are you sure you want to remove this member? This cannot be undone.'
    );
    if (!confirmRemove) return;

    try {
      const { error: removeError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', memberId);

      if (removeError) {
        throw removeError;
      }

      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    } catch (err: any) {
      console.error('Error removing member:', err);
      setError(err.message || 'Failed to remove member');
    }
  };

  // Fetch members from profiles table
  const fetchMembers = async () => {
    setLoadingMembers(true);
    setError('');

    try {
      const { data, error: selectError } = await supabase
        .from('profiles')
        .select('id, role, first_name, last_name, phone_number, email, updated_at');

      if (selectError) {
        throw selectError;
      }

      const mapped = (data ?? []).map((row: any) => ({
        id: row.id,
        role: row.role,
        first_name: row.first_name,
        last_name: row.last_name,
        phone_number: row.phone_number,
        email: row.email,
        updated_at: row.updated_at
      }));

      setMembers(mapped);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to fetch members.');
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const filteredMembers = members.filter((m) => {
    const checkStr = `${m.first_name ?? ''} ${m.last_name ?? ''} ${m.phone_number ?? ''} ${m.email ?? ''}`.toLowerCase();
    return checkStr.includes(search.toLowerCase());
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedMembers = () => {
    return [...filteredMembers].sort((a, b) => {
      // Apply appropriate sort logic based on the field
      let comparison = 0;
      
      switch (sortField) {
        case 'name':
          // Sort by full name
          const aName = `${a.first_name || ''} ${a.last_name || ''}`.trim();
          const bName = `${b.first_name || ''} ${b.last_name || ''}`.trim();
          comparison = aName.localeCompare(bName);
          break;
          
        case 'phone':
          // Sort by phone number
          const aPhone = a.phone_number || '';
          const bPhone = b.phone_number || '';
          comparison = aPhone.localeCompare(bPhone);
          break;
          
        case 'email':
          // Sort by email
          const aEmail = a.email || '';
          const bEmail = b.email || '';
          comparison = aEmail.localeCompare(bEmail);
          break;
          
        case 'role':
          // Sort by role
          comparison = a.role.localeCompare(b.role);
          break;
      }
      
      // Apply sort direction
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const handleMemberUpdated = (updatedMember: MemberRow) => {
    setMembers((prev) =>
      prev.map((m) => (m.id === updatedMember.id ? updatedMember : m))
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="mx-auto max-w-7xl py-10 px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
          <div className="flex items-center space-x-2">
            <Users className="h-6 w-6 text-indigo-600" />
            <h1 className="text-2xl font-bold tracking-tight text-gray-800">
              Manage Members
            </h1>
          </div>
        </div>

        {error && (
          <div className="mt-6 rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <section className="mt-8">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative w-full">
              <Search className="absolute left-2 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name, phone, or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {loadingMembers ? (
            <div className="flex items-center justify-center py-10">
              <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600"></div>
            </div>
          ) : filteredMembers.length === 0 ? (
            <p className="text-gray-600">No members found.</p>
          ) : (
            <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100" 
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center">
                        Name
                        {sortField === 'name' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100" 
                      onClick={() => handleSort('phone')}
                    >
                      <div className="flex items-center">
                        Phone
                        {sortField === 'phone' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100" 
                      onClick={() => handleSort('email')}
                    >
                      <div className="flex items-center">
                        Email
                        {sortField === 'email' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100" 
                      onClick={() => handleSort('role')}
                    >
                      <div className="flex items-center">
                        Role
                        {sortField === 'role' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getSortedMembers().map((member) => {
                    const displayName =
                      member.first_name || member.last_name
                        ? `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim()
                        : '—';
                    return (
                      <TableRow key={member.id} className="hover:bg-gray-50">
                        <TableCell className="whitespace-nowrap">{displayName}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          {member.phone_number || '—'}
                        </TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell className="whitespace-nowrap capitalize">{member.role}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingMember(member)}
                              title="Edit Member"
                            >
                              <Edit3 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveMember(member.id)}
                              title="Remove Member"
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </main>
      {editingMember && (
        <EditMemberModal
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onMemberUpdated={handleMemberUpdated}
        />
      )}
    </div>
  );
}